import { createRouter, createWebHashHistory } from 'vue-router';

import { ensureSession, useSession } from '@/auth/session';
import AuditView from '@/views/AuditView.vue';
import AdminUsersView from '@/views/AdminUsersView.vue';
import CommandCenterView from '@/views/CommandCenterView.vue';
import LoginView from '@/views/LoginView.vue';
import ModuleCenterView from '@/views/ModuleCenterView.vue';
import ModuleWorkbenchView from '@/views/ModuleWorkbenchView.vue';
import MyWorkView from '@/views/MyWorkView.vue';
import PortfolioView from '@/views/PortfolioView.vue';
import ProjectWorkspaceView from '@/views/project/ProjectWorkspaceView.vue';
import ProjectOverviewView from '@/views/project/ProjectOverviewView.vue';
import ProjectActivityPlanView from '@/views/project/ProjectActivityPlanView.vue';
import ProjectPpapView from '@/views/project/ProjectPpapView.vue';
import ProjectBomView from '@/views/project/ProjectBomView.vue';
import ProjectPlanView from '@/views/project/ProjectPlanView.vue';
import ProjectTaskBoardView from '@/views/project/ProjectTaskBoardView.vue';
import ProjectSectionView from '@/views/project/ProjectSectionView.vue';

const router = createRouter({
  history: createWebHashHistory(),
  scrollBehavior: () => ({ top: 0 }),
  routes: [
    { path: '/login', name: 'login', component: LoginView, meta: { title: '登录', public: true } },
    // 0929 模具开发：供应商拿链接进来，不登录（链接只看得到那一套模）
    { path: '/supplier/:token', name: 'supplier-portal', component: () => import('@/views/SupplierPortalView.vue'), meta: { title: '模具进度提交', public: true } },
    { path: '/', name: 'command', component: CommandCenterView, meta: { title: '公司级指挥台' } },
    // 全屏大屏：不带侧边栏（App.vue 按 meta.fullscreen 跳过 AppShell），仍需登录
    { path: '/bigscreen', name: 'bigscreen', component: () => import('@/views/BigScreenView.vue'), meta: { title: '项目总览大屏', fullscreen: true } },
    { path: '/portfolio', name: 'portfolio', component: PortfolioView, meta: { title: '项目组合' } },
    { path: '/my-work', name: 'my-work', component: MyWorkView, meta: { title: '我的工作' } },
    // 任务跟踪表 + 节点汇总（2026-09-03）：人派的事和计划节点分两页，都跨项目
    { path: '/tasks', name: 'task-tracker', component: () => import('@/views/TaskTrackerView.vue'), meta: { title: '任务跟踪' } },
    { path: '/tasks/delays', name: 'task-delays', component: () => import('@/views/DelayStatsView.vue'), meta: { title: '延期统计' } },
    { path: '/nodes', name: 'node-summary', component: () => import('@/views/NodeSummaryView.vue'), meta: { title: '节点汇总' } },
    { path: '/meetings', name: 'meetings', component: () => import('@/views/MeetingListView.vue'), meta: { title: '会议' } },
    { path: '/moulds', name: 'mould-board', component: () => import('@/views/MouldBoardView.vue'), meta: { title: '模具开发' } },
    {
      path: '/meetings/series',
      name: 'meeting-series',
      component: () => import('@/views/MeetingSeriesView.vue'),
      meta: { title: '会议序列管理' },
    },
    {
      path: '/meetings/:meetingId(\\d+)',
      name: 'meeting-detail',
      component: () => import('@/views/MeetingDetailView.vue'),
      meta: { title: '会议详情' },
    },
    // 0911：升级问题（升到部门负责人 / 管理层的未关问题）。人人可开，数据按可见项目收
    { path: '/escalations', name: 'escalation-board', component: () => import('@/views/EscalationBoardView.vue'), meta: { title: '升级问题' } },
    {
      path: '/director',
      name: 'director-board',
      component: () => import('@/views/DirectorBoardView.vue'),
      meta: { title: '项目总监看板' },
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('@/views/AccountSettingsView.vue'),
      meta: { title: '个人设置' },
    },
    { path: '/modules', name: 'modules', component: ModuleCenterView, meta: { title: '模块中心' } },
    {
      path: '/audit',
      name: 'audit',
      component: AuditView,
      meta: { title: '操作留痕', requiresAdmin: true },
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: AdminUsersView,
      meta: { title: '用户管理', requiresAdmin: true },
    },
    // 指挥台的健康数字和指标卡都带着筛选参数指向 /projects。
    // 这个路径此前并不存在，被最后的兜底路由吞回指挥台——点了等于没点，
    // 且测试只断言链接字符串、没真正路由过，所以一直没暴露。
    {
      path: '/projects',
      redirect: (to) => ({ path: '/portfolio', query: to.query }),
    },
    {
      path: '/projects/:projectId',
      component: ProjectWorkspaceView,
      children: [
        { path: '', redirect: { name: 'project-overview' } },
        { path: 'overview', name: 'project-overview', component: ProjectOverviewView, meta: { title: '项目概览' } },
        { path: 'tasks', name: 'project-tasks', component: ProjectTaskBoardView, meta: { title: '任务看板' } },
        { path: 'tracker', name: 'project-tracker', component: () => import('@/views/project/ProjectTaskTrackerView.vue'), meta: { title: '任务跟踪' } },
        { path: 'plan', name: 'project-plan', component: ProjectPlanView, meta: { title: '计划甘特' } },
        { path: 'activities', name: 'project-activities', component: ProjectActivityPlanView, meta: { title: '阶段工作' } },
        { path: 'deliverables', name: 'project-deliverables', component: ProjectSectionView, props: { section: 'deliverables', title: '交付物' }, meta: { title: '项目交付物' } },
        { path: 'dfmea', name: 'project-dfmea', component: ProjectSectionView, props: { section: 'dfmea', title: 'DFMEA' }, meta: { title: '项目 DFMEA' } },
        { path: 'pfmea', name: 'project-pfmea', component: ProjectSectionView, props: { section: 'pfmea', title: 'PFMEA' }, meta: { title: '项目 PFMEA' } },
        { path: 'control-plans', name: 'project-control-plans', component: ProjectSectionView, props: { section: 'control-plans', title: '控制计划' }, meta: { title: '项目控制计划' } },
        { path: 'drawings', name: 'project-drawings', component: () => import('@/views/project/ProjectDrawingsView.vue'), meta: { title: '图纸气泡' } },
        { path: 'ppap', name: 'project-ppap', component: ProjectPpapView, meta: { title: 'PPAP' } },
        { path: 'bom', name: 'project-bom', component: ProjectBomView, meta: { title: 'BOM' } },
        { path: 'moulds', name: 'project-moulds', component: () => import('@/views/project/ProjectMouldsView.vue'), meta: { title: '模具' } },
        { path: 'issues', name: 'project-issues', component: ProjectSectionView, props: { section: 'issues', title: '问题 / 8D' }, meta: { title: '问题 / 8D' } },
        { path: 'changes', name: 'project-changes', component: ProjectSectionView, props: { section: 'changes', title: '工程变更' }, meta: { title: '工程变更' } },
        { path: 'team', name: 'project-team', component: ProjectSectionView, props: { section: 'team', title: '项目团队' }, meta: { title: '项目团队' } },
        { path: 'files', name: 'project-files', component: () => import('@/views/project/ProjectFilesView.vue'), meta: { title: '项目文件' } },
      ],
    },
    { path: '/modules/projects', name: 'module-projects', component: ModuleWorkbenchView, props: { moduleKey: 'projects' }, meta: { title: '项目管理' } },
    { path: '/modules/dfmea', name: 'module-dfmea', component: ModuleWorkbenchView, props: { moduleKey: 'dfmea' }, meta: { title: 'DFMEA' } },
    { path: '/modules/pfmea-cp', name: 'module-pfmea-cp', component: ModuleWorkbenchView, props: { moduleKey: 'pfmea-cp' }, meta: { title: 'PFMEA & CP' } },
    { path: '/modules/files', name: 'module-files', component: () => import('@/views/ModuleFilesView.vue'), meta: { title: '文件管理' } },
    { path: '/modules/issues', name: 'module-issues', component: ModuleWorkbenchView, props: { moduleKey: 'issues' }, meta: { title: '问题管理' } },
    { path: '/modules/changes', name: 'module-changes', component: ModuleWorkbenchView, props: { moduleKey: 'changes' }, meta: { title: '变更管理' } },
    // 量产 BOM（1030）：移交量产的产品台账；点进去是单个产品的接收名单和同步记录
    { path: '/modules/bom', name: 'module-bom', component: () => import('@/views/BomLedgerView.vue'), meta: { title: '量产 BOM' } },
    { path: '/modules/bom/:projectId(\\d+)', name: 'module-bom-detail', component: () => import('@/views/BomLedgerView.vue'), props: true, meta: { title: '量产 BOM' } },
    {
      path: '/modules/approvals',
      name: 'module-approvals',
      component: () => import('@/views/ApprovalWorkflowView.vue'),
      // 页面里本来就只有管理员能改（canEdit = canManageUsers），入口也跟着收（0901）
      // 0916 并进了原「系统设置」里有用的两块（邮件入库、字典查看），改叫「审批流与字典」
      meta: { title: '审批流与字典', requiresAdmin: true },
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

router.beforeEach(async (to) => {
  if (to.meta.public) return true;
  if (await ensureSession()) {
    if (to.meta.requiresAdmin && !useSession().canManageUsers.value) {
      return { name: 'command', query: { denied: 'admin-users' } };
    }
    return true;
  }
  return {
    name: 'login',
    query: { redirect: to.fullPath },
  };
});

router.afterEach((to) => {
  document.title = `PDM 系统 · ${String(to.meta.title || '东禾智能')}`;
});

export default router;
